import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useContext, useEffect } from 'react';
import { GRNContext } from '../Context/GRNContext';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import { useColorScheme } from "@mui/material/styles";

import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

import ForgotPassword from './ForgetPassword';
import AppTheme from './../Shared-Theme/AppTheme';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: "calc((1 - var(--template-frame-height, 0)) * 100dvh)",
  minHeight: "100%",
  padding: theme.spacing(2),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
  "&::before": {
    content: '""',
    display: "block",
    position: "absolute",
    zIndex: -1,
    inset: 0,
    backgroundImage:
      "radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))",
    backgroundRepeat: "no-repeat",
    ...theme.applyStyles("dark", {
      backgroundImage:
        "radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))",
    }),
  },
}));

export default function SignIn(props) {
  const { login } = useContext(GRNContext);
  const navigate = useNavigate();  
  const location = useLocation();

  const [emailError, setEmailError] = React.useState(false);
  const [emailErrorMessage, setEmailErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false); 

  const { mode, setMode } = useColorScheme();

  const handleToggle = () => {
    if (!mode) return;
    setMode(mode === "light" ? "dark" : "light");
  };
  
    // 🌙 Load theme
  // useEffect(() => {
  //   const theme = localStorage.getItem("theme");
  //   if (theme === "dark") {
  //     document.documentElement.classList.add("dark");
  //   }
  // }, []);

  // const toggleTheme = () => {
  //   const newMode = mode === "light" ? "dark" : "light";
  //   setMode(newMode);
  //   localStorage.setItem("theme", newMode);
  // };

  // const handleClickOpen = () => {
  //   setOpen(true);
  // };

  // const handleClose = () => {
  //   setOpen(false);
  // };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (emailError || passwordError) return;

    const data = new FormData(event.currentTarget);
    const email = data.get('email');
    const password = data.get('password');

    const result = await login(email, password);

    if (result.success) {

      const isUser = result.user?.role === "user";

    if (isUser) {
      navigate("/records", { replace: true });
    } else {
      navigate("/", { replace: true });
      }

    } else {
      setEmailError(true);
      setEmailErrorMessage(result.message || 'Login failed');
    }
  };

  const validateInputs = () => {
    const email = document.getElementById('email');
    const password = document.getElementById('password');

    let isValid = true;

    if (!email.value || !/\S+@\S+\.\S+/.test(email.value)) {
      setEmailError(true);
      setEmailErrorMessage('Please enter a valid email address.');
      isValid = false;
    } else {
      setEmailError(false);
      setEmailErrorMessage('');
    }

    if (!password.value || password.value.length < 6) {
      setPasswordError(true);
      setPasswordErrorMessage('Password must be at least 6 characters long.');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  
const inputStyle = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "background.paper",
    "& fieldset": {
      borderColor: "#d1d5db",
    },
    "&:hover fieldset": {
      borderColor: "#75bfb0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#75bfb0",
      borderWidth: "2px",
    },
  },
  "& .MuiInputBase-input": {
    padding: "14px",
    color: "inherit",
  },
  "& input::placeholder": {
    color: "#9ca3af",
    opacity: 1,
  },
};

  return (
  <>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { md: "1fr 1fr" },
        height: "100vh",
      }}
    >
      {/* LEFT SIDE */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
        }}
      >
        <Box sx={{ position: "absolute", top: 32, left: 40 }}>
          <img src="images/plugin-logo-dark.png" alt="logo" style={{ height: 80 }} />
        </Box>

        <img
          src="images/analytics-dashboard.png"
          alt="illustration"
          style={{ maxHeight: "500px" }}
        />
      </Box>

      {/* RIGHT SIDE */}
      <Box
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100vh",
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            position: "absolute",
            top: 40,
            right: 30,
          }}
        >
          <Button variant="outlined" onClick={handleToggle}>
            {mode === "dark" ? "☀️" : "🌙"}
          </Button>
        </Box>

        {/* FORM CENTER */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            pt: 5,
          }}
        >
          <Box sx={{ width: "100%", maxWidth: 420 }}>
            <Card sx={{ color: "text.primary", }}>
              <Typography variant="h3" fontWeight="bold" mb={1} sx={{ color: "text.primary" }}>
                Sign in
              </Typography>

              <Typography variant="body2" mb={3} sx={{ color: "text.primary" }}>
                New here?
                <Link href="#" sx={{ color: "#75bfb0", pl: 0.5, }} >
                  Sign up!
                </Link>
              </Typography>

              <Box
                component="form"
                onSubmit={handleSubmit}
                sx={{ display: "flex", flexDirection: "column", gap: 2 }}
              >
                {/* Email */}
                <TextField
                  id="email"
                  name="email"
                  placeholder="Email"
                  fullWidth
                  error={emailError}
                  helperText={emailErrorMessage}
                  type="email"
                />

                {/* Password */}
                <TextField
                  id="password"
                  name="password"
                  placeholder="Password"
                  type={showPassword ? "text" : "password"}
                  fullWidth
                  error={passwordError}
                  helperText={passwordErrorMessage}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          sx={{
                            color: "text.primary",
                            "&:hover": {
                              backgroundColor: "transparent",
                              transform: "scale(1.1)",
                            },
                          }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Remember + Forgot */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        disableRipple
                        size="small"
                        sx={{
                          color: "#75bfb0",
                          "&.Mui-checked": {
                            color: "#ffffff",
                            backgroundColor: "#75bfb0",
                          },
                        }}
                      />
                    }
                    label="Remember me"
                  />

                  <Link
                    component="button"
                    onClick={() => setOpen(true)}
                    underline="none"
                    sx={{
                      color: "#75bfb0",
                      "&:hover": {
                        color: "#5fa99a",
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    Forgot your password?
                  </Link>
                </Box>

                {/* Button */}
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  sx={{
                    backgroundColor: "#75bfb0",
                    color: "#fff",
                    py: 1.5,
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 600,
                    "&:hover": {
                      opacity: 0.9,
                      backgroundColor: "#75bfb0",
                    },
                  }}
                >
                  Sign in
                </Button>
              </Box>
            </Card>
          </Box>
        </Box>

        {/* FOOTER */}
        <Typography
          textAlign="center"
          variant="body2"
          color="text.secondary"
          justifyContent="center"
          sx={{ pb: 2, color: "text.primary" }}
        >
          © Pluugin 2026
        </Typography>
      </Box>
    </Box>

    <ForgotPassword open={open} handleClose={() => setOpen(false)} />
  </>
);
}